import React from 'react';

export const readinessMetricLabel = (metric) => {
  if (metric?.status === 'not_tracked') return 'Not tracked';
  if (metric?.status === 'incomplete') return 'Incomplete';
  if (metric?.status !== 'complete' || !Number.isFinite(metric.value))
    return 'Unavailable';
  return metric.value.toLocaleString();
};
const rows = (report = {}) => [
  ['Known Camden locals', report.cohort],
  ['Email-eligible locals', report.emailEligiblePlayers],
  ['Email candidates — preferences only', report.emailPreferenceOnlyCandidates],
  ['Shared-email preference conflicts', report.emailSharedAddressConflicts],
  ['Push-registered locals', report.push?.registeredPlayers],
  ['Registered endpoints', report.push?.registeredEndpoints],
  ['Alerts off by choice', report.push?.disabledPlayers],
  ['Reconnect needed', report.push?.reconnectNeededPlayers],
  ['No registration — reason unknown', report.push?.missingRegistrationUnknown],
  ['Test attempts requested', report.tests?.attempts],
  ['Distinct players tested', report.tests?.distinctPlayers],
  ['Locals who reported receiving a test', report.tests?.reportedReceiptPlayers],
  [
    'Locals with a receipt report on a current registration',
    report.tests?.currentBindingReceiptPlayers,
  ],
  ['Provider accepted', report.tests?.accepted],
  ['Provider failed', report.tests?.failed],
  ['Tests skipped', report.tests?.skipped],
  ['Outcome unknown', report.tests?.unknown],
  ['Receipt reports (all registrations)', report.tests?.reportedReceipts],
  ['Receipt reports on current registrations', report.tests?.currentBindingReceipts],
  ['Historical receipt reports', report.tests?.historicalReceipts],
  ['Notification openings', report.opened || { status: 'not_tracked' }],
];
const taskReach = (estimate) =>
  estimate && Number.isFinite(estimate.eligibleRecipientCount)
    ? `${estimate.eligibleRecipientCount} eligible helpers in the unsaved ${
        estimate.regionLabel || estimate.regionSlug
      } ${
        estimate.taskType
      } task scenario. This is audience eligibility, not push coverage.`
    : 'Not checked. Use the existing Region fanout count below for an unsaved task scenario; audience eligibility is separate from push coverage.';
export const buildAlertReadinessReportLines = (report = {}, estimate) => [
  `- As of: ${report.asOf || 'Unavailable'}`,
  `- Test window: ${report.from || 'Unavailable'} to ${
    report.to || 'Unavailable'
  }`,
  '- Cohort: existing Camden geographic and attribution membership, before helper filtering.',
  ...rows(report).map(
    ([label, metric]) =>
      `- ${label}: ${readinessMetricLabel(metric)}${
        Number.isFinite(metric?.denominator)
          ? ` (denominator: ${metric.denominator})`
          : ''
      }`,
  ),
  '- Email eligibility awaits applicable suppressions and separate campaign history.',
  `- Task reach: ${taskReach(estimate)}`,
  '- Stored registrations and provider acceptance do not guarantee receipt, helper availability, or future delivery. Reporting windows do not expire registrations.',
];
export default function LocalOpsAlertReadiness({ report, taskEstimate }) {
  const data = report || {};
  return (
    <section
      className="ops-health-panel ops-health-alert-readiness"
      aria-labelledby="alert-readiness-title"
    >
      <div className="ops-health-section-heading">
        <h2 id="alert-readiness-title">LocalOps alert readiness</h2>
        <span>
          {data.status === 'complete'
            ? 'Stored coverage & test evidence'
            : data.status === 'incomplete'
            ? 'Incomplete'
            : 'Unavailable'}
        </span>
      </div>
      <p className="ops-health-muted">
        Known Camden locals use the existing geographic and attribution
        membership, before helper filtering. Registration coverage does not
        establish availability to work or guarantee delivery.
      </p>
      <p className="ops-health-muted">
        As of {data.asOf || 'unavailable'} · Tests from{' '}
        {data.from || 'unavailable'} to {data.to || 'unavailable'}. This
        reporting window is not a device-validity deadline.
      </p>
      <dl className="ops-health-readiness-grid">
        {rows(data).map(([label, metric]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{readinessMetricLabel(metric)}</dd>
            {Number.isFinite(metric?.denominator) && (
              <small>Denominator: {metric.denominator}</small>
            )}
          </div>
        ))}
      </dl>
      <p className="ops-health-muted">
        Email eligibility awaits applicable suppressions and separate campaign
        history. Preference-only candidates are not a send audience. Missing
        registrations do not establish permission denial.
      </p>
      <p className="ops-health-muted">{taskReach(taskEstimate)}</p>
      <p className="ops-health-muted">
        A player’s explicit receipt report is separate from provider acceptance.
        This evidence adds no helper or CAMDEN15 participation requirement.
      </p>
    </section>
  );
}
